import React, { useState, useEffect, useCallback } from 'react';
import {
  useAtomValue as useStateSegmentValue,
  useSetAtom as useStateSegmentSetter,
} from 'jotai';
import ReactDOM from 'react-dom';
import {
  RiCloseLine,
  RiFileCopyLine,
  RiInformationLine,
  RiArrowLeftSLine,
  RiArrowRightSLine,
  RiBook2Line,
} from '@remixicon/react';
import styles from './ImageContentModal.style.module.css';
import IconButton from '../../IconButton';
import DownloadIcon from '../../../atoms/DownloadIcon';
import { translate } from '../../../../localization';
import PointingTooltip, {
  TooltipPosition,
} from '../../../atoms/PointingTooltip';
import { ContentTypeImage, ImageSource } from '../../../../types/Content';
import { imageFeedbackStateSegment } from '../../../../store/markettingContentGeneration/stateSegments/imageFeedbackStateSegment';
import { contentIdStateSegment } from '../../../../store/markettingContentGeneration/stateSegments/contentListSegment';
import { submitImageFeedback } from '../../../../services/ReferenceFeedback';
import { ImageFeedbackState } from '../../../../types/ReferenceFeedback';
import { getSessionId } from '../../../../utils/commonUtils';
import { getEncodedUserNtid } from '../../../../store/markettingContentGeneration/helpers/common';
import InfoTooltip from '../../InfoTooltip/InfoTooltip';
import { TooltipPosition as ArrowTooltipPosition } from '../../../atoms/ArrowTooltip';
import ThumbsIcon from '../../../atoms/ThumbsIcon';
import { ImagesData } from '../../../../store/markettingContentGeneration/stateSegments/imagesDataStateSegment';

interface ImageContentModalProps {
  imageSrc: string;
  imageAlt: string;
  content?: string;
  onClose: () => void;
  current: number;
  total: number;
  handlePageChange: (newIndex: number) => void;
  handleImageDownload: (blobData: Blob, name: string) => void;
  onImageDownload?: (src: string, title: string) => void;
  imageData?: ContentTypeImage;
  imagesData: ImagesData;
}

const ImageContentModal: React.FC<ImageContentModalProps> = ({
  imagesData,
  imageSrc,
  imageAlt,
  content,
  onClose,
  current,
  total,
  handlePageChange,
  handleImageDownload,
  onImageDownload,
  imageData,
}) => {
  const [isTooltipVisible, setTooltipVisible] = useState(false);
  const [resolution, setResolution] = useState<string | null>(null);

  // Feedback state from global store
  const imageId = imageData?.id || imageData?.imageMetadata?.documentId;
  const contentId = useStateSegmentValue(contentIdStateSegment);
  const docId = imageData?.imageMetadata?.documentId;
  const docTitle = imageData?.imageMetadata?.documentTitle;
  const sessionId = getSessionId();
  const usersClientId = getEncodedUserNtid();
  const feedbackStateMap = useStateSegmentValue(imageFeedbackStateSegment);
  const setFeedbackStateMap = useStateSegmentSetter(imageFeedbackStateSegment);
  const feedbackState: ImageFeedbackState =
    imageId && feedbackStateMap[imageId]
      ? feedbackStateMap[imageId]
      : { isLiked: false, isDisliked: false, isLoading: false };

  const isExtLibSource =
    imageData?.source === ImageSource.EXT_LIB || !!imageData?.imageMetadata;
  const imageMetadata = imageData?.imageMetadata;

  const showTooltip = () => setTooltipVisible(true);
  const hideTooltip = () => setTooltipVisible(false);
  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget;
    setResolution(`${img.naturalWidth} x ${img.naturalHeight}`);
  };

  // Handle feedback submission
  const handleImageFeedback = useCallback(
    async (isLike: boolean) => {
      if (!contentId || !imageId || !sessionId || !usersClientId) return;
      setFeedbackStateMap(prev => ({
        ...prev,
        [imageId]: {
          isLiked: isLike,
          isDisliked: !isLike,
          isLoading: true,
        },
      }));
      try {
        await submitImageFeedback({
          id: contentId,
          imageId,
          sessionId,
          usersClientId,
          action: isLike ? 'like' : 'dislike',
          isLiked: isLike,
          isDisliked: !isLike,
          forContentFabric: true,
          docId,
          docTitle,
        });
        setFeedbackStateMap(prev => ({
          ...prev,
          [imageId]: {
            isLiked: isLike,
            isDisliked: !isLike,
            isLoading: false,
          },
        }));
      } catch {
        setFeedbackStateMap(prev => ({
          ...prev,
          [imageId]: {
            isLiked: false,
            isDisliked: false,
            isLoading: false,
          },
        }));
      }
    },
    [
      contentId,
      imageId,
      sessionId,
      usersClientId,
      docId,
      docTitle,
      setFeedbackStateMap,
    ]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft' && current > 1) {
        handlePageChange(current - 2);
      } else if (event.key === 'ArrowRight' && current < total) {
        handlePageChange(current);
      } else if (event.key === 'Escape') {
        onClose();
      }
    },
    [current, total, handlePageChange, onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden'; // prevent background scroll

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto'; // restore scroll
    };
  }, [handleKeyDown]);

  const modalContent = (
    <div
      className={styles.backdrop}
      role='dialog'
      aria-modal='true'
      aria-label='Image Viewer Modal'
    >
      <div className={styles.modal}>
        {/* Extended Knowledge Badge - Top Left */}
        {isExtLibSource && (
          <div className={styles.extKnowledgeBadge}>
            <RiBook2Line size={16} className={styles.extKnowledgeIcon} />
            <span>{translate('extended_knowledge')}</span>
          </div>
        )}

        <div className={styles.topButtonContainer}>
          <IconButton
            onClick={() =>
              onImageDownload
                ? onImageDownload(imageSrc, imageAlt)
                : imageData &&
                  handleImageDownload(
                    imagesData[imageData?.id].blobData,
                    imageData?.name || imageAlt
                  )
            }
            className={styles.downloadBtn}
            ariaLabel='Download Image'
            type='button'
          >
            <DownloadIcon />
            {translate('download')}
          </IconButton>

          <button
            className={styles.closeButton}
            onClick={onClose}
            type='button'
            aria-label='Close Modal'
          >
            <RiCloseLine />
          </button>
        </div>

        <div className={styles.imageSectionContainer}>
          <div>
            <IconButton
              className={styles.navButton}
              onClick={() => handlePageChange(current - 2)}
              disabled={current - 1 === 0}
              ariaLabel='Previous Image'
              type='button'
            >
              <RiArrowLeftSLine />
            </IconButton>
          </div>

          <div className={styles.gridContainer}>
            {/* Image Counter */}
            <div className={styles.imageCounterRow}>
              <div className={styles.imageCounter}>
                <span className={styles.imageCounterBold}>
                  {translate('image_option')} {current}
                </span>
                <span className={styles.imageCounterLight}> / {total}</span>
              </div>
            </div>

            {/* Image Section */}
            <div className={styles.row + ' ' + styles.images}>
              <div
                className={`${styles.imageSection} ${
                  !content ? styles.centerImage : ''
                }`}
              >
                <img
                  src={imageSrc}
                  alt={imageAlt}
                  className={styles.image}
                  onLoad={handleImageLoad}
                />
              </div>

              {content && (
                <div className={styles.contentSection}>
                  <div className={styles.contentHeadingContainer}>
                    <h3 className={styles.heading}>AI Generated Description</h3>
                    <RiFileCopyLine
                      size={24}
                      color='#0000C9'
                      className={styles.copyButtonIcon}
                    />
                  </div>
                  <p className={styles.content}>{content}</p>
                </div>
              )}
            </div>

            {/* Footer Row: Image ID | Document Title + Info | Dimensions */}
            {isExtLibSource && imageMetadata && (
              <div className={styles.imageMetadataFooter}>
                {/* Image ID - Left */}
                <div className={styles.imageIdSection}>
                  {imageData?.id && (
                    <span className={styles.imageIdText}>
                      <strong>{translate('image_id_label')}</strong>:{' '}
                      {imageData.id}
                    </span>
                  )}
                </div>

                {/* Document Title + Info Icon - Center */}
                <div className={styles.documentTitleSection}>
                  {imageMetadata.documentTitle && (
                    <>
                      <span className={styles.documentTitleText}>
                        <strong>{translate('document_title_label')}:</strong>{' '}
                        {imageMetadata.documentTitle}
                      </span>
                      <InfoTooltip
                        id='ext-lib-image-info-tooltip'
                        tooltipPosition={ArrowTooltipPosition.TOP_CENTER}
                        content={
                          <div className={styles.extLibTooltipContent}>
                            {imageMetadata.documentId && (
                              <div className={styles.extLibTooltipRow}>
                                <span className={styles.extLibTooltipLabel}>
                                  {translate('document_id_label')}:
                                </span>
                                <span>{imageMetadata.documentId}</span>
                              </div>
                            )}
                            {imageMetadata.citation && (
                              <div className={styles.extLibTooltipRow}>
                                <span className={styles.extLibTooltipLabel}>
                                  {translate('citation_label')}:
                                </span>
                                <span>{imageMetadata.citation}</span>
                              </div>
                            )}
                            {imageMetadata.uploadUserId && (
                              <div className={styles.extLibTooltipRow}>
                                <span className={styles.extLibTooltipLabel}>
                                  {translate('upload_user_id_label')}:
                                </span>
                                <span>{imageMetadata.uploadUserId}</span>
                              </div>
                            )}
                          </div>
                        }
                      />
                    </>
                  )}
                </div>

                {/* Dimensions - Right */}
                <div className={styles.dimensionsSection}>
                  {resolution && (
                    <span className={styles.dimensionsText}>{resolution}</span>
                  )}
                </div>
              </div>
            )}

            {/* Non-EXT_LIB images: Show dimensions centered */}
            {!isExtLibSource && resolution && (
              <div className={styles.row}>
                <div className={styles.imageDimensionsSection}>
                  <PointingTooltip
                    text={translate('image_carousel_tooltip_text')}
                    showTooltip={isTooltipVisible}
                    onMouseEnter={showTooltip}
                    toolTipPosition={TooltipPosition.BOTTOM_CENTER}
                    onMouseLeave={hideTooltip}
                    id='image-dimensions-tooltip'
                    classes={styles.imageDimensionsTooltip}
                  >
                    <RiInformationLine
                      size={12}
                      className={styles.tooltipInfoIcon}
                    />
                  </PointingTooltip>
                  <span className={styles.imageDimensions}>{resolution}</span>
                </div>
                <div className={styles.emptyContainer} />
              </div>
            )}

            {/* Image Feedback Pill - EXT_LIB only */}
            {isExtLibSource && (
              <div className={styles.feedbackPillContainer}>
                <div className={styles.feedbackPill}>
                  <span className={styles.imageFeedbackLabel}>
                    {translate('image_feedback_label')}
                  </span>
                  <div className={styles.imageFeedbackButtons}>
                    <button
                      type='button'
                      className={styles.feedbackButton}
                      onClick={() => handleImageFeedback(true)}
                      aria-label='Thumbs up'
                      aria-pressed={feedbackState.isLiked}
                      disabled={
                        feedbackState.isLoading || feedbackState.isLiked
                      }
                    >
                      <ThumbsIcon
                        isSelected={feedbackState.isLiked}
                        isThumbsUp
                      />
                    </button>
                    <button
                      type='button'
                      className={styles.feedbackButton}
                      onClick={() => handleImageFeedback(false)}
                      aria-label='Thumbs down'
                      aria-pressed={feedbackState.isDisliked}
                      disabled={
                        feedbackState.isLoading || feedbackState.isDisliked
                      }
                    >
                      <ThumbsIcon
                        isSelected={feedbackState.isDisliked}
                        isThumbsUp={false}
                      />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div>
            <IconButton
              className={styles.navButton}
              onClick={() => handlePageChange(current)}
              disabled={current === total}
              ariaLabel='Next Image'
              type='button'
            >
              <RiArrowRightSLine />
            </IconButton>
          </div>
        </div>
      </div>
    </div>
  );

  // Use portal
  return ReactDOM.createPortal(
    modalContent,
    document.getElementById('modal-root')!
  );
};

export default ImageContentModal;
